// packages
import { Component } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import { Elements } from '@stripe/react-stripe-js';
import scrollToComponent from 'react-scroll-to-component';

// local
import Pitch from './pitch.js';
import SupportIntent from './supportintent.js';
import ContactInfoForm from './contactinfoform.js';
import PaymentForm from './paymentform.js';
import SupportSection from './supportsection.js';
import { trackEvent } from '../analytics/helpers.js';
import { trackCheckoutStep } from './analytics.js';
import http from '$shared/http.js';
import { isMobileViewport } from '$shared/utils.js';
import { receivingBenefit, getAmountWithFees } from './shared.js';
import { getInitialState } from './state.js';

const STEPS = ['intent', 'contact', 'payment', 'complete'];

const stripePromise = loadStripe(window.TMPStripePublishableKey);

export default class SupportPage extends Component {
  constructor(props) {
    super(props);
    this.state = getInitialState();
  }

  markStepComplete(step) {
    let completedSteps = this.state.completedSteps;
    let firstCompletion = false;

    // Send GA virtual page view if this is the first time we've completed this step
    if (!completedSteps.includes(step)) {
      completedSteps = [...completedSteps, step];
      firstCompletion = true;
    }
    const nextStep = STEPS.find((step) => !completedSteps.includes(step));

    this.setState({ completedSteps, step: nextStep }, () => {
      trackCheckoutStep(step, this.state);
      this.scrollToSection(nextStep);
    });
  }

  handleActivate(section) {
    this.setState({ step: section }, () => {
      this.scrollToSection(section);
    });
    trackEvent('edit_completed_checkout_section', { section });
  }

  scrollToSection(section) {
    if (!isMobileViewport()) {
      return;
    }
    scrollToComponent(this[section], {
      align: 'top',
      offset: -80,
      duration: 300,
    });
  }

  intentComplete(data) {
    this.setState(
      {
        donationAmount: data.donationAmount,
        donationFrequency: data.donationFrequency,
        benefitDesired: data.benefitDesired,
        coverFees: data.coverFees,
      },
      () => this.fetchPaymentIntent()
    );
    this.markStepComplete('intent');
  }

  contactInfoComplete(info) {
    this.setState(
      {
        contactInfo: info,
      },
      () => this.save()
    );
    this.markStepComplete('contact');
  }

  fetchPaymentIntent() {
    http
      .post('/donate/payment-intent', {
        json: {
          amount: getAmountWithFees(
            this.state.donationAmount,
            this.state.coverFees
          ),
          frequency: this.state.donationFrequency,
          paymentIntentId: this.state.paymentIntentId,
        },
      })
      .json()
      .then((data) => {
        if (data.clientSecret) {
          this.setState({
            clientSecret: data.clientSecret,
            paymentIntentId: data.id,
          });
        }
      })
      .catch(async (error) => {
        const data = await error.response.json();

        console.log('Error fetching payment intent', data);
        this.setState({
          clientSecret: null,
          error: data.error || 'Something went wrong processing your donation.',
        });
      });
  }

  save() {
    http
      .post('/donate/save-details', {
        json: {
          inboundVia: this.state.inboundVia,
          amount: getAmountWithFees(
            this.state.donationAmount,
            this.state.coverFees
          ),
          coverFees: this.state.coverFees,
          paymentIntentId: this.state.paymentIntentId,
          email: this.state.contactInfo.email,
          contactInfo: this.state.contactInfo,
          frequency: this.state.donationFrequency.toLowerCase(),
          benefitDesired: this.state.benefitDesired,
        },
      })
      .json()
      .then(() => {
        this.setState({
          error: null,
        });

        window.history.pushState(
          null,
          '',
          `/donate?payment_intent=${this.state.paymentIntentId}&client_secret=${this.state.clientSecret}&donate_step=payment`
        );
      })
      .catch(async (error) => {
        let errorMessage;
        try {
          const data = await error.response.json();
          errorMessage = data.error;
        } catch (e) {
          errorMessage = 'Something went wrong processing your donation.';
        }

        this.setState({
          error: errorMessage,
        });
      });
  }

  getStripeOptions() {
    return {
      clientSecret: this.state.clientSecret,
      appearance: {
        theme: 'night',
        variables: {
          colorPrimary: '#ff0b3a',
          colorDanger: '#ff0b3a',
        },
      },
    };
  }

  render() {
    return (
      <div className="support-page-react">
        {this.renderNotice()}
        {!this.state.error && this.renderSections()}
      </div>
    );
  }

  renderSections() {
    return (
      <div className="support-container">
        {this.state.step === 'intent' && (
          <Pitch copyOptions={this.props.options} />
        )}
        <SupportSection
          active={this.state.step === 'intent'}
          complete={this.state.completedSteps.includes('intent')}
          onActivate={this.handleActivate.bind(this, 'intent')}
        >
          <SupportIntent
            enableTotes={this.props.options.enableTotes}
            active={this.state.step === 'intent'}
            complete={this.state.completedSteps.includes('intent')}
            savedDonationAmount={this.state.donationAmount}
            savedDonationFrequency={this.state.donationFrequency}
            coverFees={this.state.coverFees}
            onComplete={this.intentComplete.bind(this)}
          />
        </SupportSection>
        <SupportSection
          active={this.state.step === 'contact'}
          complete={this.state.completedSteps.includes('contact')}
          onActivate={this.handleActivate.bind(this, 'contact')}
        >
          <ContactInfoForm
            contactInfo={this.state.contactInfo}
            onComplete={this.contactInfoComplete.bind(this)}
            complete={this.state.completedSteps.includes('contact')}
            active={this.state.step === 'contact'}
            receivingBenefit={
              this.state.benefitDesired &&
              receivingBenefit(
                this.state.donationAmount,
                this.state.donationFrequency
              )
            }
          />
        </SupportSection>
        <SupportSection
          active={this.state.step === 'payment'}
          complete={this.state.completedSteps.includes('payment')}
          onActivate={this.handleActivate.bind(this, 'payment')}
        >
          <Elements options={this.getStripeOptions()} stripe={stripePromise}>
            {this.state.clientSecret && (
              <PaymentForm
                contactInfo={this.state.contactInfo}
                savedPaymentToken={this.state.paymentToken}
                active={this.state.step === 'payment'}
                complete={this.state.completedSteps.includes('payment')}
                amount={this.state.donationAmount}
                frequency={this.state.donationFrequency}
                coverFees={this.state.coverFees}
              />
            )}
          </Elements>
        </SupportSection>
      </div>
    );
  }

  renderNotice() {
    if (this.state.error) {
      return (
        <div className="support-container">
          <div className="error-message">{this.state.error}</div>
          <p>
            If the problem persists please contact{' '}
            <a href="mailto:support@themarshallproject.org">
              support@themarshallproject.org
            </a>
          </p>
        </div>
      );
    } else if (this.state.notice) {
      return (
        <div className="support-container">
          <div className="error-message">{this.state.notice}</div>
          <p>
            Please correct the problem below. If the problem persists please
            contact{' '}
            <a href="mailto:support@themarshallproject.org">
              support@themarshallproject.org
            </a>
          </p>
        </div>
      );
    }
  }
}
